import { HomeZone } from "../types";

/**
 * Utility class for handling home zone authorization logic
 * Provides centralized functionality for checking if users have access to rooms/seats based on home zones
 */
export class HomeZoneAuthUtil {
    /**
     * Cache for user's home zones to avoid repeated API calls
     */
    private static _cachedHomeZones: HomeZone[] | null = null;

    /**
     * API service instance for fetching home zones
     */
    private static _apiService: any = null;

    /**
     * Initialize the utility with an API service instance
     * @param apiService The API service instance to use for fetching home zones
     */
    public static initialize(apiService: any): void {
        HomeZoneAuthUtil._apiService = apiService;
    }

    /**
     * Fetches and caches user's home zones using the initialized API service
     * @returns Promise resolving to user's home zones
     */
    public static async getUserHomeZones(): Promise<HomeZone[]> {
        if (!HomeZoneAuthUtil._apiService) {
            throw new Error("HomeZoneAuthUtil must be initialized with an API service before use");
        }

        if (!HomeZoneAuthUtil._cachedHomeZones) {
            HomeZoneAuthUtil._cachedHomeZones =
                await HomeZoneAuthUtil._apiService.employee.fetchOwnHomeZones();
        }
        return HomeZoneAuthUtil._cachedHomeZones;
    }

    /**
     * Clears the cached home zones (useful when user changes or for testing)
     */
    public static clearCache(): void {
        HomeZoneAuthUtil._cachedHomeZones = null;
    }

    /**
     * Normalizes home zones into a set of comparable keys (id and name, case-insensitive)
     * @param homeZones Array of home zones to normalize
     * @returns Set of normalized keys for comparison
     */
    private static normalizeHomeZoneKeys(homeZones: HomeZone[]): Set<string> {
        const keys = new Set<string>();

        const addKey = (val?: any) => {
            if (!val) {
                return;
            }
            if (typeof val === "string") {
                keys.add(val);
                keys.add(val.toLowerCase());
            } else if (typeof val === "object") {
                if (val.id) {
                    keys.add(String(val.id));
                }
                if (val.name) {
                    keys.add(String(val.name));
                    keys.add(String(val.name).toLowerCase());
                }
            }
        };

        if (Array.isArray(homeZones)) {
            homeZones.forEach(addKey);
        } else if (homeZones && typeof homeZones === "object") {
            const arr = homeZones as HomeZone[];
            if (Array.isArray(arr)) {
                arr.forEach(addKey);
            }
        }

        return keys;
    }

    /**
     * Checks if a user is authorized to access a room based on home zones
     * @param roomHomeZones Home zones associated with the room
     * @param userHomeZones User's home zones
     * @returns true if user is authorized, false otherwise
     */
    public static isUserAuthorizedForRoom(
        roomHomeZones: HomeZone[],
        userHomeZones: HomeZone[]
    ): boolean {
        // If room has no home zone restrictions, show to all users
        if (!roomHomeZones || roomHomeZones.length === 0) {
            return true;
        }

        // Normalize user's home zones for comparison
        const userZoneKeys = HomeZoneAuthUtil.normalizeHomeZoneKeys(userHomeZones);

        // Authorized if ANY of the room's home zones matches user's home zones (by id or name)
        for (const hz of roomHomeZones) {
            if (!hz) {
                continue;
            }
            const id = hz.id != null ? String(hz.id) : undefined;
            const name = hz.name != null ? String(hz.name) : undefined;
            if (
                (id && userZoneKeys.has(id)) ||
                (name && (userZoneKeys.has(name) || userZoneKeys.has(name.toLowerCase())))
            ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Filters an array of spaces (seats/rooms) based on home zone authorization (synchronous)
     * @param spaces Array of spaces to filter
     * @param userHomeZones User's home zones
     * @param getRoomHomeZones Function to extract room home zones from a space object
     * @returns Filtered array of authorized spaces
     */
    public static filterSpacesByHomeZoneSync<T>(
        spaces: T[],
        userHomeZones: HomeZone[],
        getRoomHomeZones: (space: T) => HomeZone[]
    ): T[] {
        return spaces.filter((space) => {
            const roomHomeZones = getRoomHomeZones(space);
            return HomeZoneAuthUtil.isUserAuthorizedForRoom(roomHomeZones, userHomeZones);
        });
    }

    /**
     * High-level method to filter spaces by home zone using the initialized API service
     * @param spaces Array of spaces to filter
     * @param getRoomHomeZones Function to extract room home zones from a space object
     * @returns Promise resolving to filtered array of authorized spaces
     */
    public static async filterSpacesByHomeZone<T>(
        spaces: T[],
        getRoomHomeZones: (space: T) => HomeZone[]
    ): Promise<T[]> {
        try {
            const userHomeZones = await HomeZoneAuthUtil.getUserHomeZones();
            return HomeZoneAuthUtil.filterSpacesByHomeZoneSync(
                spaces,
                userHomeZones,
                getRoomHomeZones
            );
        } catch (error) {
            console.error("Error filtering spaces by home zone:", error);
            // Return all spaces if there's an error to avoid blocking functionality
            return spaces;
        }
    }

    /**
     * Identifies unauthorized seats from a list of seats (synchronous)
     * @param seats Array of seats to check
     * @param userHomeZones User's home zones
     * @returns Object containing unauthorized seats and their IDs
     */
    public static identifyUnauthorizedSeatsSync(
        seats: any[],
        userHomeZones: HomeZone[]
    ): { unauthorizedSeats: any[]; unauthorizedSeatIds: string[] } {
        const unauthorizedSeats = seats.filter((seat) => {
            const roomHomeZones = seat?.room?.homeZones || [];
            return !HomeZoneAuthUtil.isUserAuthorizedForRoom(roomHomeZones, userHomeZones);
        });

        const unauthorizedSeatIds = unauthorizedSeats.map((seat) => seat.id);

        return {
            unauthorizedSeats,
            unauthorizedSeatIds
        };
    }

    /**
     * Identifies unauthorized seats from a list of seats using the initialized API service
     * @param seats Array of seats to check
     * @returns Promise resolving to object containing unauthorized seats and their IDs
     */
    public static async identifyUnauthorizedSeats(
        seats: any[]
    ): Promise<{ unauthorizedSeats: any[]; unauthorizedSeatIds: string[] }> {
        try {
            const userHomeZones = await HomeZoneAuthUtil.getUserHomeZones();
            return HomeZoneAuthUtil.identifyUnauthorizedSeatsSync(seats, userHomeZones);
        } catch (error) {
            console.error("Error identifying unauthorized seats:", error);
            // Return empty arrays if there's an error to avoid blocking functionality
            return {
                unauthorizedSeats: [],
                unauthorizedSeatIds: []
            };
        }
    }
}
